import { Box, type BoxProps, Text } from "@hightouch/htui";
import type { ReactNode } from "react";
import { Link } from "src/router";

const DISCLAIMER_CONTAINER = "agent-disclaimer";
// Measures the disclaimer's own box, about 368px wide in the 400px agent sidebar.
const NARROW_DISCLAIMER_QUERY = `@container ${DISCLAIMER_CONTAINER} (max-width: 400px)`;
// The type scale starts at 12px; 9px fits the legal copy on one line in the sidebar.
const NARROW_DISCLAIMER_FONT_SIZE = "9px";

const DefaultDisclaimer = () => (
  <>
    Agents can make mistakes and use is subject to the{" "}
    <Link
      fontSize="inherit"
      lineHeight="inherit"
      href={`${import.meta.env.VITE_WEBSITE_URL}/ai-addendum`}
      isExternal
    >
      AI terms
    </Link>
    . Double-check responses.
  </>
);

type LlmDisclaimerProps = {
  disclaimer?: ReactNode;
} & BoxProps;

export const LlmDisclaimer = ({
  disclaimer = <DefaultDisclaimer />,
  sx,
  ...rest
}: LlmDisclaimerProps) => (
  <Box
    minWidth={0}
    width="100%"
    // `sx` because `containerType` has no Chakra style prop.
    sx={{
      containerType: "inline-size",
      containerName: DISCLAIMER_CONTAINER,
      ...sx,
    }}
    {...rest}
  >
    <Box
      as={Text}
      // Block, so lineHeight applies: inline, the span sits in this wrapper's ~20px line box.
      display="block"
      fontSize="x-small"
      // lineHeight 4 (16px): the Text default reads airy under the composer.
      lineHeight={4}
      color="text.tertiary"
      sx={{
        [NARROW_DISCLAIMER_QUERY]: {
          fontSize: NARROW_DISCLAIMER_FONT_SIZE,
          lineHeight: "none",
          // 8px insets the line from the composer's edges.
          px: 2,
          // The 9px line box leaves less room above than the wide 16px one.
          pt: 2,
        },
      }}
    >
      {disclaimer}
    </Box>
  </Box>
);
